// Per-user data isolation: each user's contacts/properties/assignments/templates
// live under storage keys namespaced by their username, e.g. `ira_contacts__jane`.
// Admin views/manages a selected user's data by passing that user's name as scope.
import { HOLIDAY_IDS } from './assignmentUtils';

export function scopedKey(base, scope) {
  return scope ? `${base}__${scope}` : base;
}

// Built-in system reminders: the two data-driven ones plus the date-anchored
// holidays. Admin edits the canonical "master" copy (stored under the admin's
// scope); each user owns a private copy seeded from it.
export const SYSTEM_TEMPLATE_IDS = ['new-closing', 'sale-anniversary', 'happy-birthday', ...HOLIDAY_IDS];

// Default wording for the built-in reminders, per language (English, Chinese,
// Spanish), seeded at the system scope and cascaded to orgs/agents. Send-time picks
// the contact's language and falls back to English. Only {{First Name}} (and
// {{Birthday}}, auto-localized) are used — both resolve in RunReminders.buildJobs.
export const DEFAULT_SYSTEM_MESSAGES = {
  // Partnership-framed (works for buyer- AND seller-side closings), like the anniversary.
  'new-closing': {
    en: "Hi {{First Name}}, congratulations again on your recent closing! Now that things have had a little time to settle, I just wanted to check in and say it was a true pleasure working with you on this milestone. If anything has come up since — questions, paperwork, or anything I can help with — I'm always just a call away. Here's to the next chapter!",
    zh: "{{First Name}}，再次恭喜您顺利完成交易！这些天想必一切都渐渐安顿下来了。能在这个重要时刻与您同行，我深感荣幸。如果之后有任何问题——无论是文件、后续事项，还是其他需要帮忙的地方——随时给我打电话。祝您开启美好的新篇章！",
    es: "Hola {{First Name}}, ¡felicidades de nuevo por el cierre! Ahora que todo se va asentando, quería saludarte y decirte que fue un verdadero placer acompañarte en este paso tan importante. Si ha surgido cualquier cosa — dudas, trámites o lo que necesites — estoy a solo una llamada de distancia. ¡Por el nuevo capítulo!",
  },
  'sale-anniversary': {
    en: "Hi {{First Name}}, happy {{Nth}} anniversary in your home! It's hard to believe it's already been {{Years}} years. I hope the house has been everything you hoped for and filled with good memories. It was a genuine pleasure helping you find it, and I'm always just a call away if there's ever anything you need.",
    zh: "{{First Name}}，乔迁 {{Years}} 周年快乐！时间过得真快，转眼已经 {{Years}} 年了。希望这个家一直让您称心如意，也充满了美好的回忆。当初能帮您找到它，我深感荣幸；今后无论有任何需要，我都随时为您效劳。",
    es: "Hola {{First Name}}, ¡feliz aniversario número {{Years}} en tu hogar! Cuesta creer que ya hayan pasado {{Years}} años. Espero que la casa haya sido todo lo que esperabas y se haya llenado de buenos recuerdos. Fue un verdadero placer ayudarte a encontrarla, y siempre estoy a una llamada de distancia para lo que necesites.",
  },
  'happy-birthday': {
    en: "Happy Birthday, {{First Name}}! I hope your day is filled with the people you love, a little time to celebrate, and everything you've been looking forward to. Wishing you a wonderful year ahead — thank you for letting me be part of your journey.",
    zh: "{{First Name}}，生日快乐！愿您今天能与所爱的人相伴，留些时间好好庆祝，所期盼的一切都如愿以偿。祝您在新的一岁里事事顺心——也谢谢您让我有幸参与您的人生旅程。",
    es: "¡Feliz cumpleaños, {{First Name}}! Espero que tu día esté lleno de las personas que quieres, un momento para celebrar y todo lo que esperabas con ilusión. Te deseo un año maravilloso por delante, y gracias por dejarme ser parte de tu camino.",
  },
  'holiday-new-year': {
    en: "Happy New Year, {{First Name}}! As one year closes and another begins, I want to wish you good health, happiness, and every success in the months ahead. Thank you for your trust this past year — I look forward to staying in touch and being here whenever you need me.",
    zh: "{{First Name}}，新年快乐！辞旧迎新之际，祝您身体健康、阖家幸福，在新的一年里万事顺遂、心想事成。感谢您过去一年的信任——期待与您常联系，也愿在您需要时随时为您效劳。",
    es: "¡Feliz Año Nuevo, {{First Name}}! Cuando un año termina y otro comienza, quiero desearte salud, felicidad y mucho éxito en los meses que vienen. Gracias por tu confianza este último año; espero seguir en contacto y estar aquí siempre que me necesites.",
  },
  'holiday-valentines': {
    en: "Happy Valentine's Day, {{First Name}}! Wishing you a day full of warmth, love, and the company of the people who mean the most to you. Thinking of you and sending my very best.",
    zh: "{{First Name}}，情人节快乐！愿您今天充满温暖与爱意，与最重要的人相伴左右。在此送上我最诚挚的祝福，也惦念着您。",
    es: "¡Feliz Día de San Valentín, {{First Name}}! Te deseo un día lleno de cariño, amor y la compañía de las personas que más significan para ti. Pensando en ti y enviándote mis mejores deseos.",
  },
  'holiday-july4': {
    en: "Happy Fourth of July, {{First Name}}! I hope you get to spend the day relaxing with family and friends, with good food and a few fireworks. Wishing you and your loved ones a fun and safe holiday.",
    zh: "{{First Name}}，独立日快乐！愿您能与家人朋友共度轻松愉快的一天，享受美食与绚烂的烟花。祝您和家人节日快乐、平安喜乐。",
    es: "¡Feliz Cuatro de Julio, {{First Name}}! Espero que puedas pasar el día disfrutando con familia y amigos, con buena comida y algunos fuegos artificiales. Te deseo a ti y a los tuyos un día festivo divertido y seguro.",
  },
  'holiday-thanksgiving': {
    en: "Happy Thanksgiving, {{First Name}}! This time of year is a good reminder to pause and appreciate the people around us — and I count clients like you among the things I'm grateful for. I hope your holiday is warm, restful, and spent with the people you love.",
    zh: "{{First Name}}，感恩节快乐！这个时节总让人停下脚步，感恩身边的人——而像您这样的客户，正是我心怀感激的理由之一。愿您的节日温暖、安宁，与所爱的人共度美好时光。",
    es: "¡Feliz Día de Acción de Gracias, {{First Name}}! Esta época del año nos invita a detenernos y valorar a quienes nos rodean, y clientes como tú están entre las cosas por las que estoy agradecido. Espero que pases unas fiestas cálidas, tranquilas y en compañía de tus seres queridos.",
  },
  'holiday-christmas': {
    en: "Merry Christmas, {{First Name}}! Wishing you and your loved ones a holiday season full of warmth, joy, and time spent together. Thank you for your trust and friendship this year — it truly means a lot. Here's to a happy and healthy year ahead.",
    zh: "{{First Name}}，圣诞快乐！愿您与家人共度一个温馨、欢乐、相伴相依的节日。衷心感谢您这一年的信任与情谊，这对我意义非凡。也预祝您新的一年幸福安康。",
    es: "¡Feliz Navidad, {{First Name}}! Te deseo a ti y a tus seres queridos una temporada llena de calidez, alegría y momentos compartidos. Gracias por tu confianza y amistad este año; de verdad significa mucho. Por un año nuevo feliz y lleno de salud.",
  },
};

// Older English defaults — used to detect an UNEDITED built-in so seeding can upgrade
// it to the current copy without ever clobbering wording an agent has customized.
const LEGACY_SYSTEM_MESSAGES_EN = {
  // May be a string or a list of past defaults — any exact match counts as "unedited"
  // and is upgraded to the current copy.
  'sale-anniversary':     [
    "Hi {{First Name}}, congratulations on another year in your home! It's been a pleasure working with you, and I'm always here if you ever need anything.",
    "Hi {{First Name}}, happy anniversary in your home! It's hard to believe how quickly the year has gone. I hope the house has been everything you hoped for and filled with good memories. It was a genuine pleasure helping you find it, and I'm always just a call away if there's ever anything you need.",
  ],
  'happy-birthday':       "Happy Birthday, {{First Name}}! Wishing you a wonderful day and a fantastic year ahead.",
  'holiday-new-year':     "Happy New Year, {{First Name}}! Wishing you health, happiness, and success in the year ahead.",
  'holiday-valentines':   "Happy Valentine's Day, {{First Name}}! Wishing you a day full of warmth and joy.",
  'holiday-july4':        "Happy Fourth of July, {{First Name}}! Wishing you and your family a fun and safe holiday.",
  'holiday-thanksgiving': "Happy Thanksgiving, {{First Name}}! I'm grateful for clients like you — wishing you a warm holiday with family and friends.",
  'holiday-christmas':    "Merry Christmas, {{First Name}}! Wishing you and your loved ones a joyful holiday season.",
};

// Past NON-English defaults, so an unedited zh/es built-in can be upgraded too (the
// EN map above can't detect those). Only ids whose localized copy changed need entries.
const LEGACY_SYSTEM_MESSAGES_L10N = {
  zh: {
    'sale-anniversary': "{{First Name}}，乔迁周年快乐！时间过得真快，转眼又是一年。希望这个家一直让您称心如意，也充满了美好的回忆。当初能帮您找到它，我深感荣幸；今后无论有任何需要，我都随时为您效劳。",
  },
  es: {
    'sale-anniversary': "Hola {{First Name}}, ¡feliz aniversario en tu hogar! Cuesta creer lo rápido que ha pasado el año. Espero que la casa haya sido todo lo que esperabas y se haya llenado de buenos recuerdos. Fue un verdadero placer ayudarte a encontrarla, y siempre estoy a una llamada de distancia para lo que necesites.",
  },
};

// Seed the default built-in messages (all 3 languages) into a scope's templates.
// Fill-only, EXCEPT an English message still equal to the old default is upgraded to
// the new copy. Never overwrites a message an agent has edited.
export function ensureSystemDefaultMessages(sysScope) {
  if (!sysScope) return;
  const key = scopedKey('ira_templates', sysScope);
  let msgs = {};
  try { msgs = JSON.parse(localStorage.getItem(key) || '{}'); } catch {}
  let changed = false;
  for (const [id, byLang] of Object.entries(DEFAULT_SYSTEM_MESSAGES)) {
    for (const lang of ['en', 'zh', 'es']) {
      const k = `${id}__${lang}`;
      const cur = msgs[k];
      const legacy = lang === 'en' ? LEGACY_SYSTEM_MESSAGES_EN[id] : (LEGACY_SYSTEM_MESSAGES_L10N[lang] || {})[id];
      const legacyList = Array.isArray(legacy) ? legacy : (legacy != null ? [legacy] : []);
      const upgradeUnedited = legacyList.includes(cur);
      if ((cur == null || upgradeUnedited) && msgs[k] !== byLang[lang]) {
        msgs[k] = byLang[lang];
        changed = true;
      }
    }
  }
  if (changed) localStorage.setItem(key, JSON.stringify(msgs));
}

// Private "base template" scaffolds seeded at the system level. They give the
// system admin starting points (with the right variable palettes) for building new
// reminders by duplicating them. Private, so they never cascade to orgs/users.
//
// A single general "Reminder template" carries the COMBINED contact + property
// variable palette (no holiday date-tokens). Sysadmin duplicates it to build any
// new reminder. (No Holiday Template scaffold — the 5 holidays exist as built-in
// auto-firing reminders, and date-tokens remain insertable in custom reminders.)
export const BASE_SYSTEM_TEMPLATES = [
  { id: 'tmpl__property', label: 'Reminder Template', base: true, public: false, varGroup: 'all' },
];

// Retired scaffolds, pruned from existing system scopes on load so they don't linger:
// the separate Contact template (merged into "Reminder template"), the Holiday
// Template (redundant with the built-in holiday reminders), and the old per-holiday cards.
const OBSOLETE_BASE_TEMPLATE_IDS = ['tmpl__contact', 'tmpl__holiday', 'tmpl__new-year', 'tmpl__valentines', 'tmpl__july4', 'tmpl__thanksgiving', 'tmpl__christmas'];

// Ensure the system scope has the base template scaffolds (idempotent). Also
// reconciles label/varGroup so renames (e.g. Property → Reminder template) propagate.
export function ensureSystemBaseTemplates(sysScope) {
  if (!sysScope) return;
  const key = scopedKey('ira_custom_templates', sysScope);
  let list = [];
  try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch {}
  if (!Array.isArray(list)) list = [];
  let changed = false;
  const before = list.length;
  list = list.filter(c => !OBSOLETE_BASE_TEMPLATE_IDS.includes(c.id));   // drop retired scaffolds
  if (list.length !== before) changed = true;
  for (const b of BASE_SYSTEM_TEMPLATES) {
    const existing = list.find(c => c.id === b.id);
    if (!existing) { list.push({ ...b }); changed = true; }
    else if (existing.label !== b.label || existing.varGroup !== b.varGroup) {
      existing.label = b.label; existing.varGroup = b.varGroup; existing.base = true;
      if (b.holiday) existing.holiday = true;
      changed = true;
    }
  }
  if (changed) localStorage.setItem(key, JSON.stringify(list));
}

// The data scope that holds an organization's master reminder templates. Reminders
// live under the ORG ID for every org — system or regular — a single uniform model
// (admins manage org.id directly; agents inherit it). The `accounts` arg is unused
// now; kept so existing call sites need no change.
export function masterScope(accounts, orgId) {
  return orgId || null;
}

// An admin custom template is "public" — inherited by new users — unless it's
// explicitly marked private.
export function isPublicTemplate(c) {
  return !!c && c.public !== false;
}

// Whether a built-in system template is PUBLISHED (public) at a scope. Built-ins are
// PRIVATE by default everywhere: a template is "public" only when an explicit
// `{id, builtin:true, public:true}` entry exists. No entry → not published (private).
// This makes the cascade strict at every level — a built-in flows to the next level
// down only if the current level consciously published it.
export function isBuiltinPublic(customList, id) {
  const o = (customList || []).find(c => c && c.builtin && c.id === id);
  return o ? o.public === true : false;
}

// Copy the PUBLISHED reminders from one scope to another (only filling entries the
// destination doesn't already have). Used to cascade reminders down the hierarchy:
//   system → MLS → org → agent.
// Strict recursion at EVERY level: only reminders the source explicitly published
// (public) cascade, and upon arrival they become PRIVATE — the recipient must
// consciously re-publish to pass them one level further. Built-ins are private by
// default (see isBuiltinPublic), so nothing flows below a level that didn't publish it.
export function seedTemplates(fromScope, toScope, { builtinsArrivePrivate = true } = {}) {
  if (!fromScope || !toScope || fromScope === toScope) return;

  const toMsgKey = scopedKey('ira_templates', toScope);
  const toCustomKey = scopedKey('ira_custom_templates', toScope);
  let fromMsgs = {}, toMsgs = {}, fromCustom = [], toCustom = [];
  try { fromMsgs   = JSON.parse(localStorage.getItem(scopedKey('ira_templates', fromScope)) || '{}'); } catch {}
  try { toMsgs     = JSON.parse(localStorage.getItem(toMsgKey) || '{}'); } catch {}
  try { fromCustom = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', fromScope)) || '[]'); } catch {}
  try { toCustom   = JSON.parse(localStorage.getItem(toCustomKey) || '[]'); } catch {}
  if (!Array.isArray(fromCustom)) fromCustom = [];
  if (!Array.isArray(toCustom)) toCustom = [];

  // Which built-ins cascade: only those the source has explicitly published.
  const inheritIds = new Set(SYSTEM_TEMPLATE_IDS.filter(id => isBuiltinPublic(fromCustom, id)));

  // Public custom reminders (skip built-in override entries and private/admin-only).
  const haveCustomId = new Set(toCustom.map(c => c.id));
  let customChanged = false;
  for (const c of fromCustom) {
    if (c.builtin) continue;
    if (!isPublicTemplate(c)) continue;
    inheritIds.add(c.id);
    if (!haveCustomId.has(c.id)) {
      // Copy the FULL definition so the inherited reminder keeps its variable
      // palette (e.g. varGroup 'all' = no holiday date-tokens). Carrying only
      // {id,label} made it fall back to the 'contact' palette and wrongly grow
      // the date tokens. Mirrors pushReminder().
      // Floated down from a parent → arrives as a READ-ONLY inherited mirror, matching
      // the server cascade (cascade.ts). The level must "Make a copy" to own/assign it.
      const entry = { id: c.id, label: c.label, varGroup: c.varGroup || 'contact', public: false, inherited: true };
      if (Array.isArray(c.variables)) entry.variables = [...c.variables];
      if (c.anchor) entry.anchor = c.anchor;   // copies of built-ins keep their date anchor
      if (c.interval) entry.interval = c.interval;   // custom reminders keep their cadence
      toCustom.push(entry);
      customChanged = true;
    }
  }

  let msgsChanged = false;
  for (const k of Object.keys(fromMsgs)) {
    const tid = k.slice(0, k.lastIndexOf('__'));   // strip trailing __<lang>
    if (inheritIds.has(tid) && toMsgs[k] == null) {
      toMsgs[k] = fromMsgs[k];
      msgsChanged = true;
    }
  }

  // Mark every inherited built-in as PRIVATE at the destination (once and only
  // once — skip if an override entry already exists). The recipient sees it but must
  // consciously re-publish to pass it one level further. (builtinsArrivePrivate is on
  // by default; callers never disable it — every hop lands templates Private.)
  if (builtinsArrivePrivate) {
    for (const id of inheritIds) {
      if (SYSTEM_TEMPLATE_IDS.includes(id) && !toCustom.some(c => c.builtin && c.id === id)) {
        toCustom.push({ id, builtin: true, public: false, inherited: true });   // read-only inherited mirror
        customChanged = true;
      }
    }
  }

  // Cascade the per-reminder auto-enrollable flags (default OFF) — for exactly the
  // reminders being inherited (only PUBLIC ones: public built-ins + public customs),
  // consistent with message inheritance. Only fills entries the destination lacks,
  // so later edits aren't clobbered.
  const autoKey = scopedKey('ira_autoenroll', toScope);
  let fromAuto = {}, toAuto = {};
  try { fromAuto = JSON.parse(localStorage.getItem(scopedKey('ira_autoenroll', fromScope)) || '{}'); } catch {}
  try { toAuto   = JSON.parse(localStorage.getItem(autoKey) || '{}'); } catch {}
  let autoChanged = false;
  for (const id of inheritIds) {
    if (fromAuto[id] !== undefined && toAuto[id] === undefined) { toAuto[id] = fromAuto[id]; autoChanged = true; }
  }

  // Cascade attachments for the inherited reminders (only fill missing).
  const attKey = scopedKey('ira_attachments', toScope);
  let fromAtt = {}, toAtt = {};
  try { fromAtt = JSON.parse(localStorage.getItem(scopedKey('ira_attachments', fromScope)) || '{}'); } catch {}
  try { toAtt   = JSON.parse(localStorage.getItem(attKey) || '{}'); } catch {}
  let attChanged = false;
  for (const id of inheritIds) {
    if (fromAtt[id] && !toAtt[id]) { toAtt[id] = fromAtt[id]; attChanged = true; }
  }

  if (msgsChanged) localStorage.setItem(toMsgKey, JSON.stringify(toMsgs));
  if (customChanged) localStorage.setItem(toCustomKey, JSON.stringify(toCustom));
  if (autoChanged) localStorage.setItem(autoKey, JSON.stringify(toAuto));
  if (attChanged) { try { localStorage.setItem(attKey, JSON.stringify(toAtt)); } catch {} }
}

// Stamp explicit private overrides for every built-in at a scope (fill-only:
// skips IDs that already have an override entry). Called after seeding an MLS
// from System so that "no override = public by default" never silently fires
// when the MLS later seeds an org — only reminders the MLS admin consciously
// publishes should ever cascade further.
export function ensureBuiltinsPrivate(scope) {
  if (!scope) return;
  const key = scopedKey('ira_custom_templates', scope);
  let list = [];
  try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch {}
  if (!Array.isArray(list)) list = [];
  let changed = false;
  for (const id of SYSTEM_TEMPLATE_IDS) {
    if (!list.some(c => c.builtin && c.id === id)) {
      list.push({ id, builtin: true, public: false });
      changed = true;
    }
  }
  if (changed) localStorage.setItem(key, JSON.stringify(list));
}

// Manually PUSH a single custom reminder from one scope to another — ADD ONLY:
// if the destination already has a reminder with that id, it's left untouched (the
// push never replaces an org's/agent's existing reminders). Copies the full
// definition + message bodies + auto-enrollable flag. Returns true if it was added.
export function pushReminder(fromScope, toScope, reminderId) {
  if (!fromScope || !toScope || fromScope === toScope || !reminderId) return false;
  let fromCustom = [], toCustom = [], fromMsgs = {}, toMsgs = {}, fromAuto = {}, toAuto = {};
  try { fromCustom = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', fromScope)) || '[]'); } catch {}
  try { toCustom   = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', toScope)) || '[]'); } catch {}
  if (!Array.isArray(fromCustom)) fromCustom = [];
  if (!Array.isArray(toCustom)) toCustom = [];

  const def = fromCustom.find(c => c.id === reminderId && !c.builtin && !c.base);
  if (!def) return false;                                  // only real custom reminders are pushable
  if (toCustom.some(c => c.id === reminderId)) return false;   // already present → don't replace

  try { fromMsgs = JSON.parse(localStorage.getItem(scopedKey('ira_templates', fromScope)) || '{}'); } catch {}
  try { toMsgs   = JSON.parse(localStorage.getItem(scopedKey('ira_templates', toScope)) || '{}'); } catch {}
  try { fromAuto = JSON.parse(localStorage.getItem(scopedKey('ira_autoenroll', fromScope)) || '{}'); } catch {}
  try { toAuto   = JSON.parse(localStorage.getItem(scopedKey('ira_autoenroll', toScope)) || '{}'); } catch {}

  // Full definition (label, varGroup, variables). Pushed reminders always land
  // private at the destination — the owner must publish them manually.
  const entry = { id: def.id, label: def.label, varGroup: def.varGroup || 'contact', public: false };
  if (Array.isArray(def.variables)) entry.variables = [...def.variables];
  if (def.anchor) entry.anchor = def.anchor;   // copies of built-ins keep their date anchor
  if (def.interval) entry.interval = def.interval;   // custom reminders keep their cadence
  toCustom.push(entry);
  for (const k of Object.keys(fromMsgs)) {
    if (k.slice(0, k.lastIndexOf('__')) === reminderId) toMsgs[k] = fromMsgs[k];
  }
  if (fromAuto[reminderId] !== undefined) toAuto[reminderId] = fromAuto[reminderId];

  // Carry the reminder's attachments along with it.
  let fromAtt = {}, toAtt = {};
  try { fromAtt = JSON.parse(localStorage.getItem(scopedKey('ira_attachments', fromScope)) || '{}'); } catch {}
  try { toAtt   = JSON.parse(localStorage.getItem(scopedKey('ira_attachments', toScope)) || '{}'); } catch {}
  if (fromAtt[reminderId]) toAtt[reminderId] = fromAtt[reminderId];

  localStorage.setItem(scopedKey('ira_custom_templates', toScope), JSON.stringify(toCustom));
  localStorage.setItem(scopedKey('ira_templates', toScope), JSON.stringify(toMsgs));
  localStorage.setItem(scopedKey('ira_autoenroll', toScope), JSON.stringify(toAuto));
  try { localStorage.setItem(scopedKey('ira_attachments', toScope), JSON.stringify(toAtt)); } catch {}
  return true;
}

// Propagate a single reminder from one scope (e.g. MLS) to another (e.g. org master)
// when the source admin publishes or updates a public reminder. Unlike seedTemplates
// (fill-only) or pushReminder (add-only), this OVERWRITES the destination's copy —
// but only if the destination owner has never explicitly saved/modified their copy
// (checked via the `modified` flag written by handleSave in ReminderTemplates).
// Works for both built-in and custom reminders.
export function propagateReminder(fromScope, toScope, reminderId) {
  if (!fromScope || !toScope || fromScope === toScope || !reminderId) return false;

  let fromMsgs = {}, fromCustom = [], fromAtt = {}, fromAuto = {};
  try { fromMsgs   = JSON.parse(localStorage.getItem(scopedKey('ira_templates', fromScope)) || '{}'); } catch {}
  try { fromCustom = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', fromScope)) || '[]'); } catch {}
  try { fromAtt    = JSON.parse(localStorage.getItem(scopedKey('ira_attachments', fromScope)) || '{}'); } catch {}
  try { fromAuto   = JSON.parse(localStorage.getItem(scopedKey('ira_autoenroll', fromScope)) || '{}'); } catch {}
  if (!Array.isArray(fromCustom)) fromCustom = [];

  // Only propagate if the reminder is currently public at the source.
  const isBuiltin = SYSTEM_TEMPLATE_IDS.includes(reminderId);
  if (isBuiltin && !isBuiltinPublic(fromCustom, reminderId)) return false;
  if (!isBuiltin) {
    const def = fromCustom.find(c => c.id === reminderId && !c.builtin && !c.base);
    if (!def || !isPublicTemplate(def)) return false;
  }

  let toCustom = [], toMsgs = {}, toAtt = {}, toAuto = {};
  try { toCustom = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', toScope)) || '[]'); } catch {}
  try { toMsgs   = JSON.parse(localStorage.getItem(scopedKey('ira_templates', toScope)) || '{}'); } catch {}
  try { toAtt    = JSON.parse(localStorage.getItem(scopedKey('ira_attachments', toScope)) || '{}'); } catch {}
  try { toAuto   = JSON.parse(localStorage.getItem(scopedKey('ira_autoenroll', toScope)) || '{}'); } catch {}
  if (!Array.isArray(toCustom)) toCustom = [];

  // Stop if the destination owner has explicitly saved/modified their copy.
  const existing = toCustom.find(c => c.id === reminderId);
  if (existing?.modified) return false;

  // Overwrite messages (all languages).
  let msgsChanged = false;
  for (const k of Object.keys(fromMsgs)) {
    const tid = k.slice(0, k.lastIndexOf('__'));
    if (tid === reminderId) { toMsgs[k] = fromMsgs[k]; msgsChanged = true; }
  }

  // Overwrite attachments.
  if (fromAtt[reminderId]) toAtt[reminderId] = fromAtt[reminderId];
  else delete toAtt[reminderId];

  // Overwrite auto-enroll flag.
  let autoChanged = false;
  if (fromAuto[reminderId] !== undefined) {
    toAuto[reminderId] = fromAuto[reminderId]; autoChanged = true;
  }

  // Add or refresh the custom-template entry. A propagated reminder is a READ-ONLY
  // inherited mirror at the destination (matches the server cascade) — arrives private,
  // no modified flag; the level must "Make a copy" to own/assign it.
  let customChanged = false;
  if (!existing) {
    if (isBuiltin) {
      toCustom.push({ id: reminderId, builtin: true, public: false, inherited: true });
    } else {
      const def = fromCustom.find(c => c.id === reminderId && !c.builtin && !c.base);
      if (!def) return false;
      const entry = { id: def.id, label: def.label, varGroup: def.varGroup || 'contact', public: false, inherited: true };
      if (Array.isArray(def.variables)) entry.variables = [...def.variables];
      if (def.anchor) entry.anchor = def.anchor;   // copies of built-ins keep their date anchor
      if (def.interval) entry.interval = def.interval;   // custom reminders keep their cadence
      toCustom.push(entry);
    }
    customChanged = true;
  } else {
    // Existing inherited mirror: re-assert read-only and refresh its definition (for
    // customs) without touching the destination's own relay (public) flag.
    if (existing.inherited !== true) { existing.inherited = true; customChanged = true; }
    if (!isBuiltin) {
      const def = fromCustom.find(c => c.id === reminderId && !c.builtin && !c.base);
      if (def) {
        existing.label = def.label;
        existing.varGroup = def.varGroup || 'contact';
        if (Array.isArray(def.variables)) existing.variables = [...def.variables];
        else delete existing.variables;
        if (def.anchor) existing.anchor = def.anchor; else delete existing.anchor;
        if (def.interval) existing.interval = def.interval; else delete existing.interval;
        customChanged = true;
      }
    }
  }

  if (msgsChanged)   localStorage.setItem(scopedKey('ira_templates', toScope), JSON.stringify(toMsgs));
  if (customChanged) localStorage.setItem(scopedKey('ira_custom_templates', toScope), JSON.stringify(toCustom));
  if (autoChanged)   localStorage.setItem(scopedKey('ira_autoenroll', toScope), JSON.stringify(toAuto));
  try { localStorage.setItem(scopedKey('ira_attachments', toScope), JSON.stringify(toAtt)); } catch {}
  return true;
}

const DATA_BASES = ['ira_contacts', 'ira_properties', 'ira_assignments', 'ira_custom_templates', 'ira_templates'];

// One-time migration: existing single-tenant data lives under the bare keys
// (`ira_contacts`, …). Move it into the first non-admin account's scope (the
// original user) so nothing is lost. Bare keys are kept as a backup.
export function migrateLegacyData(accounts) {
  if (localStorage.getItem('ira_scoped_v1')) return;
  const owner = (accounts.find(a => !a.admin) || accounts.find(a => a.admin) || {}).username;
  if (owner) {
    DATA_BASES.forEach(base => {
      const legacy = localStorage.getItem(base);
      const target = scopedKey(base, owner);
      if (legacy != null && localStorage.getItem(target) == null) {
        localStorage.setItem(target, legacy);
      }
    });
  }
  localStorage.setItem('ira_scoped_v1', '1');
}
